import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form } from 'react-bootstrap';
import { FaEdit, FaBan, FaSave, FaTimes, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../services/api';

import { PageHeader }      from '../../components/shared/PageHeader';
import { KpiCards }        from '../../components/shared/KpiCard';
import { SearchFilterBar } from '../../components/shared/SearchFilterBar';
import { DataTable }       from '../../components/shared/DataTable';
import { StatusSwitch }    from '../../components/shared/StatusSwitch';

// ==========================================
// CONSTANTES
// ==========================================
const FILTERS = [
  { value: 'ALL',      label: 'Todos',    variant: 'dark'      },
  { value: 'ACTIVE',   label: 'Ativos',   variant: 'success'   },
  { value: 'INACTIVE', label: 'Inativos', variant: 'secondary' },
];

// 🔧 Categorias padronizadas
const CATEGORIAS = [
  'Outdoor LED',
  'Outdoor Lona',
  'Busdoor',
  'Indoor',
  'Frontlight',
];

const COLUMNS = ['Nome', 'Categoria', 'Localidade', 'Slots', 'Status', 'Ações'];

// 🔧 Gera código único tipo "EQP-M8K2X9P1"
const generateIdentifierCode = () => {
  const timestamp = Date.now().toString(36).toUpperCase().slice(-4);
  const random    = Math.random().toString(36).toUpperCase().slice(2, 6);
  return `EQP-${timestamp}${random}`;
};

// ==========================================
// PÁGINA PRINCIPAL
// ==========================================
export default function Equipamentos() {
  const [equipamentos,       setEquipamentos]       = useState([]);
  const [searchTerm,         setSearchTerm]         = useState('');
  const [statusFilter,       setStatusFilter]       = useState('ALL');
  const [categoryFilter,     setCategoryFilter]     = useState('ALL'); // 🆕
  const [showModal,          setShowModal]          = useState(false);
  const [currentEquipamento, setCurrentEquipamento] = useState(null);
  const [loading,            setLoading]            = useState(true);

  const fetchEquipamentos = async () => {
    try {
      setLoading(true);
      const res = await api.get('/equipments');
      setEquipamentos(res.data);
    } catch {
      toast.error('Erro ao carregar lista de equipamentos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEquipamentos(); }, []);

  const filtered = equipamentos.filter(e => {
    const s = searchTerm.toLowerCase();
    const matchText =
      (e.name?.toLowerCase()     || '').includes(s) ||
      (e.category?.toLowerCase() || '').includes(s) ||
      (e.location?.toLowerCase() || '').includes(s);
    const matchStatus =
      statusFilter === 'ALL' ||
      (statusFilter === 'ACTIVE'   && e.isActive) ||
      (statusFilter === 'INACTIVE' && !e.isActive);
    const matchCategory =
      categoryFilter === 'ALL' || e.category === categoryFilter; // 🆕
    return matchText && matchStatus && matchCategory;
  });

  const handleSave = async (data) => {
    try {
      const payload = {
        name:     data.name,
        category: data.category,
        location: data.location,
        slots:    parseInt(data.slots, 10) || 1,
        isActive: data.isActive,
      };

      if (data.id) {
        // ✏️ Editar — não altera identifierCode existente
        await api.put(`/equipments/${data.id}`, payload);
        toast.success('Equipamento atualizado com sucesso!');
      } else {
        // ➕ Criar — gera código automaticamente
        payload.identifierCode = generateIdentifierCode();
        await api.post('/equipments', payload);
        toast.success('Equipamento criado com sucesso!');
      }

      setShowModal(false);
      setCurrentEquipamento(null);
      fetchEquipamentos();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar equipamento.');
    }
  };

  const handleInativar = async (id) => {
    if (!window.confirm('Tem certeza que deseja inativar este equipamento?')) return;
    try {
      await api.put(`/equipments/${id}`, { isActive: false });
      toast.success('Equipamento inativado com sucesso!');
      fetchEquipamentos();
    } catch {
      toast.error('Erro ao inativar equipamento.');
    }
  };

  const kpiItems = [
    { label: 'Total de Equipamentos', value: equipamentos.length,                          color: 'primary'   },
    { label: 'Equipamentos Ativos',   value: equipamentos.filter(e =>  e.isActive).length, color: 'success'   },
    { label: 'Equipamentos Inativos', value: equipamentos.filter(e => !e.isActive).length, color: 'secondary' },
  ];

  return (
    <Container fluid className="p-4">

      <PageHeader
        title="Gerenciamento de Equipamentos"
        buttonLabel="Novo Equipamento"
        onButtonClick={() => { setCurrentEquipamento(null); setShowModal(true); }}
      />

      <Row className="mb-4">
        <KpiCards items={kpiItems} colSize={4} />
      </Row>

      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Buscar por nome, categoria ou localidade..."
        filters={FILTERS}
        activeFilter={statusFilter}
        onFilterChange={setStatusFilter}
      />

      {/* 🆕 Filtro por Categoria */}
      <Card className="mb-3 shadow-sm">
        <Card.Body className="py-2">
          <Row className="align-items-center">
            <Col md={3}>
              <Form.Label className="small text-muted mb-1 fw-bold">
                Filtrar por Categoria
              </Form.Label>
              <Form.Select
                size="sm"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="ALL">Todas as categorias</option>
                {CATEGORIAS.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </Form.Select>
            </Col>
            {categoryFilter !== 'ALL' && (
              <Col md={9} className="d-flex align-items-end">
                <small className="text-muted mt-3">
                  Mostrando <strong>{filtered.length}</strong> equipamento{filtered.length !== 1 ? 's' : ''} da categoria <strong>{categoryFilter}</strong>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 ms-2"
                    onClick={() => setCategoryFilter('ALL')}
                  >
                    <FaTimes className="me-1" />Limpar
                  </Button>
                </small>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      <DataTable columns={COLUMNS} loading={loading} emptyMessage="Nenhum equipamento encontrado.">
        {filtered.length > 0 && filtered.map(e => (
          <tr key={e.id}>
            <td>{e.name}</td>
            <td>
              <span className="text-dark">
                {e.category}
              </span>
            </td>
            <td>{e.location}</td>
            <td>{e.slots}</td>
            <td>
              {e.isActive
                ? <span className="text-success fw-bold"><FaCheckCircle className="me-1" />Ativo</span>
                : <span className="text-secondary fw-bold"><FaBan className="me-1" />Inativo</span>}
            </td>
            <td>
              <Button variant="warning" size="sm" className="me-2"
                onClick={() => { setCurrentEquipamento(e); setShowModal(true); }}>
                <FaEdit />
              </Button>
              <Button variant="danger" size="sm"
                disabled={!e.isActive}
                onClick={() => handleInativar(e.id)}>
                <FaBan />
              </Button>
            </td>
          </tr>
        ))}
      </DataTable>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Body>
          <EquipamentoForm
            equipamento={currentEquipamento}
            onSave={handleSave}
            onCancel={() => setShowModal(false)}
          />
        </Modal.Body>
      </Modal>

    </Container>
  );
}

// ==========================================
// FORMULÁRIO
// ==========================================
function EquipamentoForm({ equipamento, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    id:       equipamento?.id       || null,
    name:     equipamento?.name     || '',
    category: equipamento?.category || '',
    location: equipamento?.location || '',
    slots:    equipamento?.slots    || 1,
    isActive: equipamento?.id ? equipamento.isActive : true,
  });

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value,
    }));
  };

  // 🛡️ Detecta categoria legada (que não está na lista padrão)
  const isLegacyCategory =
    formData.category && !CATEGORIAS.includes(formData.category);

  return (
    <Card className="border-0">
      <Card.Header className="bg-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0">{equipamento ? 'Editar Equipamento' : 'Novo Equipamento'}</h5>
        <StatusSwitch
          value={formData.isActive}
          onChange={(v) => setFormData(prev => ({ ...prev, isActive: v }))}
        />
      </Card.Header>

      <Card.Body>
        <Form onSubmit={(e) => { e.preventDefault(); onSave(formData); }}>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Nome do Equipamento *</Form.Label>
                <Form.Control name="name" value={formData.name} onChange={handleChange}
                  placeholder="Ex: Outdoor BR-116 Km 50" required />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Qtd. de Slots *</Form.Label>
                <Form.Control type="number" name="slots" value={formData.slots}
                  onChange={handleChange} min={1} required />
                <Form.Text className="text-muted">Mídias exibidas simultaneamente.</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Categoria *</Form.Label>
                <Form.Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                >
                  <option value="">Selecione uma categoria...</option>
                  {CATEGORIAS.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                  {/* 🛡️ Mantém a categoria antiga visível se for legado */}
                  {isLegacyCategory && (
                    <option value={formData.category}>
                      {formData.category} (legado)
                    </option>
                  )}
                </Form.Select>
                {isLegacyCategory && (
                  <Form.Text className="text-warning">
                    ⚠️ Categoria antiga. Considere atualizar para uma das opções padrão.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Localidade / Endereço *</Form.Label>
                <Form.Control name="location" value={formData.location} onChange={handleChange}
                  placeholder="Rua, Bairro ou Coordenadas..." required />
              </Form.Group>
            </Col>
          </Row>

          <hr />
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button variant="secondary" onClick={onCancel}>
              <FaTimes className="me-1" /> Cancelar
            </Button>
            <Button variant="success" type="submit">
              <FaSave className="me-2" /> Salvar
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}
